import React, { useState, useEffect } from 'react';
import {
  Paper,
  Stack,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Box,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  PlayArrow as TestIcon,
  History as HistoryIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { API } from '../../lib/api';

export default function PromptLibrary({ onCreateNew, onEditPrompt, onTestPrompt }) {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [viewDetailsDialog, setViewDetailsDialog] = useState(false);

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    setLoading(true);
    setError('');
    
    try {
      const promptsData = await API.prompts.list();
      
      // Fetch detailed data for each prompt to get content
      const detailedPrompts = await Promise.all(
        promptsData.map(async (prompt) => {
          try {
            const detail = await API.prompts.get(prompt.key);
            const latestVersion = detail.versions?.find(v => v.version === prompt.latest_version) || detail.versions?.[0];
            return {
              ...prompt,
              content: latestVersion?.content,
              status: latestVersion?.status || 'draft',
              versions: detail.versions
            };
          } catch (err) {
            console.error(`Failed to load details for ${prompt.key}:`, err);
            return prompt; // Return basic data if detail fetch fails
          }
        })
      );
      
      setPrompts(detailedPrompts);
    } catch (err) {
      console.error('Failed to load prompts:', err);
      setError('Failed to load prompts: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'submitted': return 'info';
      case 'review_ok': return 'warning';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  const formatPromptContent = (content) => {
    if (typeof content === 'string') {
      return content;
    }
    if (content?.template) {
      return content.template;
    }
    if (content === null || content === undefined) {
      return 'No content available';
    }
    return JSON.stringify(content, null, 2) || 'Invalid content';
  };

  const copyPromptKey = (key) => {
    navigator.clipboard.writeText(key);
  };

  const viewPromptDetails = async (promptKey) => {
    try {
      const detail = await API.prompts.get(promptKey);
      setSelectedPrompt(detail);
      setViewDetailsDialog(true);
    } catch (err) {
      console.error('Failed to load prompt details:', err);
      setError('Failed to load prompt details: ' + err.message);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading your prompts...</Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Header */}
      <Paper sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" gutterBottom>
              Your Prompt Library
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your AI prompts, create versions, and track performance
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreateNew}
            size="large"
          >
            Create New Prompt
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error">{error}</Alert>
      )}

      {/* Empty State */}
      {prompts.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 500, mx: 'auto' }}>
          <Typography variant="h6" gutterBottom>
            No prompts yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first AI prompt to get started with testing and optimization
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreateNew}
          >
            Create Your First Prompt
          </Button>
        </Paper>
      )}

      {/* Prompt Cards */}
      {prompts.length > 0 && (
        <Grid container spacing={3}>
          {prompts.map((prompt) => (
            <Grid item xs={12} md={6} lg={4} key={prompt.key}>
              <Card sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                minHeight: 280,
                maxHeight: 400,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <CardContent sx={{ flex: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                      {prompt.key.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Typography>
                    <Tooltip title="Copy prompt key">
                      <IconButton size="small" onClick={() => copyPromptKey(prompt.key)}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {prompt.key}
                  </Typography>

                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Chip 
                      label={`v${prompt.latest_version || 1}`} 
                      size="small" 
                      variant="outlined"
                    />
                    <Chip 
                      label={prompt.status || 'draft'}
                      size="small" 
                      color={getStatusColor(prompt.status)}
                    />
                  </Stack>

                  {prompt.description && (
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {prompt.description}
                    </Typography>
                  )}

                  <Box sx={{ 
                    bgcolor: '#1b173027', 
                    p: 1.5, 
                    borderRadius: 1, 
                    fontFamily: 'monospace', 
                    fontSize: '0.8rem',
                    minHeight: 80,
                    maxHeight: 80,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'flex-start'
                  }}>
                    <Typography variant="body2" sx={{ 
                      fontFamily: 'inherit',
                      fontSize: 'inherit',
                      lineHeight: 1.4,
                      color: 'text.primary',
                      backgroundColor: 'transparent',
                    }}>
                      {(formatPromptContent(prompt.content) || 'No content available').substring(0, 150)}
                      {(formatPromptContent(prompt.content) || '').length > 150 && '...'}
                    </Typography>
                  </Box>
                </CardContent>

                <CardActions>
                  <Button 
                    size="small" 
                    startIcon={<EditIcon />}
                    onClick={() => onEditPrompt && onEditPrompt(prompt)}
                  >
                    Edit
                  </Button>
                  <Button 
                    size="small" 
                    startIcon={<TestIcon />}
                    onClick={() => onTestPrompt && onTestPrompt(prompt)}
                  >
                    Test
                  </Button>
                  <Button 
                    size="small" 
                    startIcon={<HistoryIcon />}
                    onClick={() => viewPromptDetails(prompt.key)}
                  >
                    History
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Prompt Details Dialog */}
      <Dialog 
        open={viewDetailsDialog} 
        onClose={() => setViewDetailsDialog(false)}
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          Prompt: {selectedPrompt?.key}
        </DialogTitle>
        <DialogContent>
          {selectedPrompt && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Typography variant="h6">Versions</Typography>
              {selectedPrompt.versions?.map((version) => (
                <Paper key={version.id} sx={{ p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="subtitle1">Version {version.version}</Typography>
                    <Chip label={version.status} color={getStatusColor(version.status)} />
                  </Stack>
                  
                  <Box sx={{ 
                    bgcolor: 'grey.50', 
                    p: 2, 
                    borderRadius: 1, 
                    fontFamily: 'monospace', 
                    fontSize: '0.9rem',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {typeof version.content === 'object' 
                      ? JSON.stringify(version.content, null, 2)
                      : version.content}
                  </Box>
                </Paper>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}